import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import {DataGridActionTypes, DataGridColumnAlignments, DataGridColumnTypes, DataGridExoticColumnFields, useDataGridSavedState} from "../../../../../../packages/data-grid";
import {ShippingFeesFreightCompaniesListDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import Button from "../../../../../components/base/button/button";
import {Add} from "@mui/icons-material";
import SearchInput from "../../../../../components/base/input/search_input";
import PartnerDataGrid from "../../../../../containers/data-grid";
import UpsertFreightCompanyDialog from "../../../../../components/dialogs/upsert-freight-comapny-dialog";
import Api from "../../../../../../core/services/api/api";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {useTranslation} from "react-i18next";

const initUpsertDialogState = {open: false, data: null};

const exportingChunkSize = 50;

const dataGridSaveConfig = {
    name: 'ShippingFeesCompaniesListViewDataGrid',
    version: 3,
}


/**
 * Freight Companies list action names
 */
const ShippingFeesFreightCompaniesActionNames = {
    Edit: 'edit',
    Delete: 'delete'
}


const ShippingFeesCompaniesListView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.shipping-fees.companies'});
    const [data, setData] = useState([]);
    const [upsertDialogState, setUpsertDialogState] = useState(initUpsertDialogState);
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<DataGridController>}*/
    const dataGridControllerRef = useRef();
    const {configureNotebookTags} = useConfigureNotebook('shipping-fees-companies-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportShippingFeesList = operatorAccessLevels.packages.sales.exportShippingFeesList;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.shippingFeesCompanies);
        configureBehavioralTool((tags) => tags.shippingFeesCompanies);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: DataGridExoticColumnFields.selection,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    width: 100,
                    resizable: false,
                    visibilityToggleable: false,
                    reorderable: false,
                    sortable: false,
                },
                {
                    title: "Code",
                    name: ShippingFeesFreightCompaniesListDataGridColumnNames.code,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: "Title",
                    name: ShippingFeesFreightCompaniesListDataGridColumnNames.title,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 140,
                    sortable: false,
                },
                {
                    title: t('columns.noCarrierFee'),
                    name: ShippingFeesFreightCompaniesListDataGridColumnNames.noCarrierFee,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.boolean,
                    width: 180,
                    sortable: false,
                },
            ],
        [t])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    /**
     * Creates the request body for the api call
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareApiRequest = useCallback((args) => {
        return {
            pagination: args.paginationInfo,
            keyword: args.filters?.keyword ?? null,
        }
    }, [])

    /**
     * Gets the list of entries based on the filters from the user.
     * If the function is being called for export, the pagination info is passed as the input
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareApiRequest(args);
        const response = await Api.getFreightCompanies(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.length ?? 0,
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [prepareApiRequest, isMounted])

    /**
     * Searches among the entries based on the filters from the operator.
     * @type {UseSearchFunction}
     */
    const searchEntries = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);


    const searchData = useSearch(searchEntries, initialSearchDataSettings)

    /**
     * Opens the dialog for adding a new freight company to the system
     */
    const onAddNewCompanyClicked = useCallback(() => {
        setUpsertDialogState({open: true, data: null});
    }, [])

    /**
     * Searches over list of drivers in the system by setting the query params according to the received keyword
     * @param keyword
     */
    const searchWithKeywords = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword?.trim(),
        });
    }, [searchData])

    /**
     * Exports the list of records.
     * Calls the function for formatting the data
     * @return {Promise<*>}
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag)
                return;

            if (!res?.data?.length)
                break;

            entries.push(...res.data);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        if (!entries.length)
            return;

        return {
            title: 'Freight Companies List',
            body: entries.map((e, i) => ({
                '': i + 1,
                'Code': e.code ?? '',
                'Title': e.title ?? '',
                [t('exportNoCarrierFees')]: e.noCarrierFee ? 'Yes' : 'No',
            }))
        }
    }, [fetchEntries, isMounted, searchData, t])

    /**
     * Data grid rows entities
     * @type {unknown}
     */
    const dataGridRows = useMemo(() => {
        return data?.map(company => ({
            key: company.id,
            data: company,
            cells: {
                [DataGridExoticColumnFields.selection]: null,
                [ShippingFeesFreightCompaniesListDataGridColumnNames.code]: company.code,
                [ShippingFeesFreightCompaniesListDataGridColumnNames.title]: company.title,
                [ShippingFeesFreightCompaniesListDataGridColumnNames.noCarrierFee]: !!company.noCarrierFee,
            }
        }))
    }, [data])

    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: ShippingFeesFreightCompaniesActionNames.Edit,
                title: 'Edit',
                visibilityConstraints: {
                    minSelectedRows: 1,
                    maxSelectedRows: 1,
                },
            },
            {
                name: ShippingFeesFreightCompaniesActionNames.Delete,
                title: 'Delete',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [])

    /**
     * Opens dialog for editing the company information.
     * @param selectedCompany
     */
    const editSelectedFreightCompany = useCallback((selectedCompany) => {
        setUpsertDialogState({open: true, data: selectedCompany});
    }, [])

    /**
     * Calls the api regarding removing the list of companies form the list
     * @param companyIds list of ids associated to the companies to be removed
     * @private
     */
    const removeFreightCompanies = useCallback(async (companyIds) => {
        searchData.setLoading(true);
        const response = await Api.deleteFreightCompanies({ids: companyIds});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(e => !companyIds.includes(e.id)))
            searchData.setPaginationInfo(prevState => ({
                ...prevState,
                length: prevState.length - companyIds.length,
            }));
        }
        dataGridControllerRef.current?.resetSelection();
        searchData.setLoading(false);
    }, [isMounted, searchData])

    /**
     * Opens the confirmation dialog regarding this action
     * If the process was confirmed, calls the remove
     */
    const getRemoveConfirmation = useCallback((itemsToBeRemoved, allRecordsSelected) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`Are you sure that you want to remove the selected freight company${itemsToBeRemoved?.length > 1 || allRecordsSelected ? '(s)' : ''}?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeFreightCompanies(itemsToBeRemoved),
        );
    }, [removeFreightCompanies])


    /**
     * Adds or updates the current list of freight companies based on the provided (upserted) company.
     * @param company
     */
    const onCompanyUpserted = useCallback((company) => {
        setData(prevState => {
            const foundIndex = prevState.findIndex(e => e.id === company.id);
            if (foundIndex === -1) {
                return [company, ...prevState];
            }
            return prevState.map(e => e.id === company.id ? {...e, ...company} : e)
        })
        dataGridControllerRef.current?.resetSelection();
    }, [])

    /**
     * Fetches all the companies given the available length of the companies.
     * @param pageLength
     * @return {Promise<*[]>}
     */
    const getAllCompanies = useCallback(async (pageLength) => {
        const _paginationInfo = {
            length: pageLength,
            pageSize: 20,
            currentPage: 1,
        };
        let entries = [];
        while (pageLength > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                continue;
            }
            if (!res?.data?.length) {
                break;
            }
            entries.push(...res?.data ?? []);
            if (!isMounted())
                return entries;
            _paginationInfo.currentPage++;
        }
        return entries;
    }, [fetchEntries, isMounted, searchData])

    /**
     * Handles the action over the selected row in the table based on the name of the action
     * @param action
     * @param selectedRows
     * @param excludedRows
     * @param allRowsSelected
     * @param pagination
     * @return {Promise<void>}
     */
    const onActionInvoked = async (action, selectedRows, excludedRows, allRowsSelected, pagination) => {
        switch (action.name) {
            case ShippingFeesFreightCompaniesActionNames.Edit: {
                if (allRowsSelected && data.length === 1)
                    editSelectedFreightCompany(data[0]);
                else
                    editSelectedFreightCompany(selectedRows[0].data);
                break;
            }
            case ShippingFeesFreightCompaniesActionNames.Delete: {
                if (!allRowsSelected) {
                    // not all rows are selected, therefore, the selectedRows list is not empty
                    return getRemoveConfirmation(selectedRows.map(e => e.data.id));
                }
                if (pagination.pageSize >= pagination.length) {
                    // all the items are within a single page, so we have the data in hand
                    return getRemoveConfirmation(data.map(e => e.id));
                }
                getRemoveConfirmation((await getAllCompanies(pagination.length)).map(e => e.id));
                break;
            }
            default:
                break;
        }
    }

    return (
        <>
            <div className={'freight-companies-list-view'}>
                <div className={'page-title-section'}>
                    <h2 className={'text-xl font-weight-semi-bold m-0'}>
                        Freight Companies
                    </h2>
                    <Button
                        className={'d-flex align-items-center justify-content-center'}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={onAddNewCompanyClicked}
                    >
                        <Add className='icon small mr-2'/>
                        <p className={'button-label'}>Add a new Company</p>
                    </Button>
                </div>
                <SearchInput
                    loading={searchData.loading}
                    initialValue={searchData.filters?.keyword}
                    autoSearch
                    immediateSearch
                    search={searchWithKeywords}
                    label={'Search'}
                />
                <div className={'mb-3'}/>
                <PartnerDataGrid
                    ref={dataGridControllerRef}
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    footerActions={footerActions}
                    hidePagination
                    hidePageSize
                    hideFooterActions={data?.length < 1}
                    onActionInvoked={onActionInvoked}
                    visibleRows={data.length}
                    state={{
                        sortBy: searchData.orderBy,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                    getExportInfo={canExportShippingFeesList ? exportToCsv : undefined}
                    onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
            <UpsertFreightCompanyDialog
                setOpen={() => setUpsertDialogState(initUpsertDialogState)}
                open={upsertDialogState.open}
                data={upsertDialogState.data}
                onAdd={onCompanyUpserted}
            />
        </>
    );
}

export default ShippingFeesCompaniesListView;
